import {Color, Icon} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import {runSuggestedQuestion} from 'relevant-items/runSuggestedQuestion';
import {generateSuggestedQuestions, SuggestedQuestion} from 'relevant-items/SuggestedQuestionsDAO';

/**
 * Loading skeleton that mirrors the three question buttons with a shimmer animation
 */
function QuestionsSkeleton(): JSX.Element {
  return (
    <>
      <div className={css(styles.questionsList)}>
        {[0, 1, 2].map((index) => (
          <div
            key={index}
            className={css(styles.skeletonQuestion)}
            // Background needs to be inline to avoid Aphrodite adding !important
            style={{
              background: `linear-gradient(90deg, ${Color.GRAY_100} 25%, ${Color.GRAY_50} 50%, ${Color.GRAY_100} 75%)`,
              backgroundSize: '200% 100%',
            }}
          />
        ))}
      </div>
      <div className={css(styles.disclaimer)}>
        <LLMLoadingMessage text="Suggesting questions you can ask..." />
      </div>
    </>
  );
}

interface AIGeneratedQuestionsProps {
  fileId: string;
  fileName: string;
}

/**
 * Displays AI-generated questions about the folder item.
 * Clicking a question opens the AI sidepanel in Ask mode with the question pre-populated.
 */
export default function AIGeneratedQuestions({fileId, fileName}: AIGeneratedQuestionsProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [questions, setQuestions] = useState<Array<SuggestedQuestion>>([]);
  const [error, setError] = useState<string | null>(null);
  const [submittingQuestionIndex, setSubmittingQuestionIndex] = useState<number | null>(null);
  const [traceId, setTraceId] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchQuestions(): Promise<void> {
      setIsLoading(true);
      setError(null);
      const response = await generateSuggestedQuestions(fileId);

      if (isMounted) {
        setQuestions(response.questions);
        setTraceId(response.traceId);
        setIsLoading(false);

        // Track when suggested questions are displayed
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.DISPLAYED_AI_GENERATED_QUESTIONS, {
          fileId,
          questionCount: response.questions.length,
          traceId: response.traceId,
        });
      }
    }

    // Call without awaiting - errors will be unhandled promise rejections (logged to console)
    fetchQuestions();

    return () => {
      isMounted = false;
    };
  }, [fileId]);

  return (
    <div
      className={css(styles.questionsContainer)}
      data-test-component="AIGeneratedQuestions"
      data-test-element="root"
    >
      <div className={css(styles.header)}>
        <div className={css(styles.title)}>
          <h4 className={css(styles.titleText)}>Suggested Questions</h4>
          <Icon icon="sparkle" className={css(styles.sparkle)} />
        </div>
      </div>
      {isLoading ? (
        <QuestionsSkeleton />
      ) : error ? (
        <div className={css(styles.errorContainer)}>
          <p className={css(styles.errorMessage)}>{error}</p>
        </div>
      ) : questions.length > 0 ? (
        <>
          <div className={css(styles.questionsList)}>
            {questions.map((item, index) => (
              <button
                key={index}
                className={css(styles.questionButton)}
                onClick={async () => {
                  setSubmittingQuestionIndex(index);
                  try {
                    await runSuggestedQuestion(item.question, fileId, fileName);
                    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_AI_GENERATED_QUESTION, {
                      fileId,
                      traceId,
                    });
                  } finally {
                    setSubmittingQuestionIndex(null);
                  }
                }}
                disabled={submittingQuestionIndex === index}
                data-test-component="AIGeneratedQuestions"
                data-test-element={`question-${index}`}
                type="button"
              >
                {submittingQuestionIndex === index ? (
                  <LLMLoadingMessage text="Opening chat..." />
                ) : (
                  item.question
                )}
              </button>
            ))}
          </div>
          <p className={css(styles.disclaimer)}>
            AI-generated questions to help you explore this item. Click a question to start a chat with Ask.
          </p>
        </>
      ) : (
        <div className={css(styles.errorContainer)}>
          <p className={css(styles.errorMessage)}>No questions generated.</p>
        </div>
      )}
    </div>
  );
}

const shimmerKeyframes = {
  '0%': {
    backgroundPosition: '200% 0',
  },
  '100%': {
    backgroundPosition: '-200% 0',
  },
};

const styles = StyleSheet.create({
  questionsContainer: {
    backgroundColor: Color.GRAY_50,
    borderColor: Color.GRAY_300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 16,
    padding: 16,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  titleText: {
    fontWeight: 600,
    margin: 0,
  },
  sparkle: {
    color: Color.PURPLE_500,
    height: 18,
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
  },
  errorContainer: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
  },
  errorMessage: {
    color: Color.GRAY_600,
    margin: 0,
  },
  questionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  questionButton: {
    backgroundColor: Color.WHITE,
    border: `1px solid ${Color.GRAY_300}`,
    borderRadius: 4,
    color: Color.GRAY_900,
    cursor: 'pointer',
    padding: 12,
    textAlign: 'left',
    ':hover': {
      backgroundColor: Color.GRAY_100,
      borderColor: Color.PURPLE_300,
    },
    ':active': {
      backgroundColor: Color.GRAY_200,
    },
    ':disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
  },
  disclaimer: {
    color: Color.GRAY_500,
    fontSize: 12,
    margin: 0,
    marginTop: 4,
  },
  skeletonQuestion: {
    height: 42,
    backgroundColor: Color.WHITE,
    border: `1px solid ${Color.GRAY_300}`,
    borderRadius: 4,
    animationName: [shimmerKeyframes],
    animationDuration: '2s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
});
